import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Calculator, Camera, Clock, Flag, Loader2, MapPin, MessageCircle, Printer, Star, Trash2, Truck, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useFarm } from '../hooks/useFarm';
import { useFarmOrder } from '../hooks/useFarmOrder';
import { useFarmPhotos } from '../hooks/useFarmPhotos';
import { getOrCreateThread } from '../hooks/useChat';
import { calcFarmPricing } from '../lib/pricing';
import { MATERIALS, SPECIALIZATIONS, QUOTE_FILE_ACCEPT, QUOTE_FILE_RE, QUOTE_FILE_HINT, MODELING_TYPES } from '../lib/machinesCatalog';
import { formatBRL, formatDate } from '@/lib/format';
import { useAuth } from '@/hooks/useAuth';

// ─── Simulador ────────────────────────────────────────────────────────────────

function Simulator({
  costs,
  machines,
  reservations,
  offersModeling,
  onReserve,
}: {
  costs:     NonNullable<ReturnType<typeof useFarm>['data']>['costs'];
  machines:  NonNullable<ReturnType<typeof useFarm>['data']>['machines'];
  reservations: Record<string, number>;
  offersModeling: boolean;
  onReserve: (supplier: number, fee: number, total: number, specs: Record<string, unknown>, projectFile: File | null) => void | Promise<void>;
}) {
  const [material,   setMaterial]   = useState('PLA');
  const [hours,      setHours]      = useState(2);
  const [grams,      setGrams]      = useState(100);
  const [machineIdx, setMachineIdx] = useState(0);
  const [deliveryKm, setDeliveryKm] = useState<number | undefined>(undefined);
  // Detalhes do projeto (obrigatórios para a farm avaliar a cotação)
  const [quantity,   setQuantity]   = useState(1);
  const [colors,     setColors]     = useState('');
  const [notes,      setNotes]      = useState('');
  const [projectFile, setProjectFile] = useState<File | null>(null);
  const [fileErr, setFileErr]       = useState('');
  const [wantsModeling, setWantsModeling] = useState(false);
  const [modelingType, setModelingType] = useState<'organic' | 'parametric'>('organic');
  const [machinesToRent, setMachinesToRent] = useState(1);
  const [atestado, setAtestado]     = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submitReserve(
    supplier: number, fee: number, total: number, specs: Record<string, unknown>,
  ) {
    if (submitting) return;          // impede múltiplos pedidos em cliques repetidos
    setSubmitting(true);
    try {
      await onReserve(supplier, fee, total, specs, projectFile);
    } finally {
      setSubmitting(false);
    }
  }

  if (!costs) return <p className="text-sm text-ink-muted">Custos não cadastrados nesta farm.</p>;

  const selectedMachine = machines[machineIdx];
  const reservedForSel = selectedMachine ? (reservations[`${selectedMachine.brand}|||${selectedMachine.model}`] ?? 0) : 0;
  const availableForSel = Math.max(0, (selectedMachine?.quantity ?? 0) - reservedForSel);
  const soldOut = availableForSel <= 0;
  const maxMachines = Math.max(1, availableForSel);
  const rentCount = soldOut ? 0 : Math.min(Math.max(1, machinesToRent), maxMachines);
  // Total de horas informado assume 1 máquina; alugar N máquinas divide o prazo.
  const wallClockHours = rentCount > 0 ? hours / rentCount : hours;

  const modelingFee = wantsModeling
    ? (modelingType === 'organic'
        ? (costs.modeling_organic_fee_cents ?? 0)
        : (costs.modeling_parametric_fee_cents ?? 0))
    : 0;

  const pricing = calcFarmPricing({
    hourly_machine_cost_cents:  costs.hourly_machine_cost_cents,
    hours_estimated:            hours,
    setup_fee_cents:            costs.setup_fee_cents,
    filament_grams:             grams,
    filament_cost_per_kg_cents: 8000,
    filament_markup_pct:        costs.filament_markup_pct,
    delivery_fee_base_cents:    costs.delivery_fee_base_cents ?? 0,
    delivery_fee_per_km_cents:  costs.delivery_fee_per_km_cents ?? 0,
    delivery_km:                deliveryKm,
    modeling_fee_cents:         modelingFee,
  });

  const modelingNeedsFile = wantsModeling && !projectFile;
  const atestadoNeedsFile = atestado && !projectFile;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFileErr('');
    const f = e.target.files?.[0] ?? null;
    if (f && !QUOTE_FILE_RE.test(f.name)) {
      setFileErr(`Formato não aceito. Envie: ${QUOTE_FILE_HINT}.`);
      e.target.value = '';
      return;
    }
    if (f && f.size > 50 * 1024 * 1024) {
      setFileErr('Arquivo muito grande (máx. 50 MB).');
      e.target.value = '';
      return;
    }
    setProjectFile(f);
  }

  // Bloco de upload de arquivo, reaproveitado nos dois modos
  const fileBlock = (
    <div className="col-span-2">
      <label className="block text-xs text-ink-muted mb-1">
        Arquivo do projeto {(wantsModeling || atestado)
          ? <span className="text-negative">* (obrigatório)</span>
          : '— opcional'}
        <span className="block text-[11px] text-ink-soft">Aceita: {QUOTE_FILE_HINT}. Máx. 50 MB.</span>
      </label>
      <input
        type="file"
        accept={QUOTE_FILE_ACCEPT}
        className="block w-full text-xs text-ink-muted file:mr-3 file:btn-secondary file:!py-1.5 file:!px-3 file:!text-xs file:border-0 file:rounded-xl file:cursor-pointer"
        onChange={handleFileChange}
      />
      {projectFile && <p className="text-xs text-positive mt-1">{projectFile.name} pronto para envio.</p>}
      {fileErr && <p className="text-xs text-negative mt-1">{fileErr}</p>}
      {modelingNeedsFile && !fileErr && (
        <p className="text-xs text-warning mt-1">Anexe a referência/desenho técnico para solicitar a modelagem.</p>
      )}
      {atestadoNeedsFile && !fileErr && (
        <p className="text-xs text-warning mt-1">O documento é obrigatório para a farm montar seu orçamento.</p>
      )}
    </div>
  );

  return (
    <div className="card p-5 space-y-4">
      <h3 className="font-semibold text-ink flex items-center gap-2">
        <Calculator className="w-4 h-4 text-brand-700" />
        Simulador de preço
      </h3>

      {/* Atestado de leigo — deixa a farm montar o orçamento */}
      <label className="flex items-start gap-2 text-sm text-ink cursor-pointer rounded-xl bg-warning/10 border border-warning/30 px-3 py-2.5">
        <input type="checkbox" className="mt-0.5" checked={atestado} onChange={(e) => setAtestado(e.target.checked)} />
        <span>
          <span className="font-medium">Não sei os detalhes técnicos (atestado de leigo)</span>
          <span className="block text-xs text-ink-muted mt-0.5">
            Deixe que a farm avalie seu documento e monte o orçamento completo para você. Basta informar a
            quantidade de peças e anexar um documento com a peça desejada.
          </span>
        </span>
      </label>

      {atestado ? (
        /* ─── Modo leigo: só quantidade + documento obrigatório ─── */
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs text-ink-muted mb-1">Quantidade de peças</label>
            <input type="number" min={1} className="input w-full" value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))} />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-ink-muted mb-1">Observações para a farm</label>
            <textarea className="input w-full" rows={3} maxLength={600}
              placeholder="Descreva o que você precisa: tamanho aproximado, uso, prazo desejado…"
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {fileBlock}
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-ink-muted mb-1">Material</label>
          <select className="input w-full" value={material} onChange={(e) => setMaterial(e.target.value)}>
            {MATERIALS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">Máquina</label>
          <select className="input w-full" value={machineIdx} onChange={(e) => { setMachineIdx(Number(e.target.value)); setMachinesToRent(1); }}>
            {machines.map((m, i) => {
              const av = Math.max(0, m.quantity - (reservations[`${m.brand}|||${m.model}`] ?? 0));
              return (
                <option key={m.id} value={i}>{m.brand} {m.model} — {av}/{m.quantity} livre{av !== 1 ? 's' : ''}</option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">Horas de impressão (em 1 máquina)</label>
          <input type="number" min={0.5} step={0.5} className="input w-full" value={hours}
            onChange={(e) => setHours(Number(e.target.value))} />
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">
            Máquinas a alugar {soldOut ? '(esgotado)' : `(máx. ${maxMachines})`}
          </label>
          {soldOut ? (
            <p className="text-xs text-negative bg-negative/10 border border-negative/20 rounded-xl px-3 py-2">
              Todas as unidades deste modelo estão reservadas no momento.
            </p>
          ) : (
            <select className="input w-full" value={rentCount} onChange={(e) => setMachinesToRent(Number(e.target.value))}>
              {Array.from({ length: maxMachines }).map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">Filamento estimado (g)</label>
          <input type="number" min={1} className="input w-full" value={grams}
            onChange={(e) => setGrams(Number(e.target.value))} />
        </div>
        <div className="flex items-end">
          <div className="w-full rounded-xl bg-brand-50 border border-brand-200 px-3 py-2">
            <p className="text-[11px] text-ink-muted">Prazo estimado de impressão</p>
            <p className="text-sm font-semibold text-brand-800">
              ~{Number.isInteger(wallClockHours) ? wallClockHours : wallClockHours.toFixed(1)}h
              <span className="font-normal text-ink-muted"> com {rentCount} máquina{rentCount > 1 ? 's' : ''}</span>
            </p>
          </div>
        </div>
        {costs.delivery_offered && (
          <div className="col-span-2">
            <label className="block text-xs text-ink-muted mb-1">
              Distância para entrega (km) — opcional
            </label>
            <input type="number" min={0} className="input w-full" placeholder="Deixe vazio para retirada"
              value={deliveryKm ?? ''} onChange={(e) => setDeliveryKm(e.target.value ? Number(e.target.value) : undefined)} />
          </div>
        )}
        <div>
          <label className="block text-xs text-ink-muted mb-1">Quantidade de peças</label>
          <input type="number" min={1} className="input w-full" value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))} />
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">Cores desejadas</label>
          <input type="text" className="input w-full" placeholder="ex: preto e vermelho"
            value={colors} onChange={(e) => setColors(e.target.value)} maxLength={120} />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-ink-muted mb-1">Observações para a farm</label>
          <textarea className="input w-full" rows={2} maxLength={600}
            placeholder="Detalhes do projeto, acabamento, prazo desejado…"
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        {offersModeling && (
          <div className="col-span-2 space-y-2">
            <label className="flex items-start gap-2 text-sm text-ink cursor-pointer rounded-xl bg-brand-50 border border-brand-200 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={wantsModeling}
                onChange={(e) => setWantsModeling(e.target.checked)}
              />
              <span>
                <span className="font-medium">Quero o serviço de modelagem 3D</span>
                <span className="block text-xs text-ink-muted mt-0.5">
                  O dono da farm modela o produto para você. O valor da modelagem é somado ao orçamento.
                </span>
              </span>
            </label>
            {wantsModeling && (
              <div className="rounded-xl border border-brand-200 bg-surface p-3 space-y-2">
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Tipo de modelagem *</label>
                  <select className="input w-full" value={modelingType}
                    onChange={(e) => setModelingType(e.target.value as 'organic' | 'parametric')}>
                    {MODELING_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                        {t.value === 'organic' && (costs.modeling_organic_fee_cents ?? 0) > 0 ? ` — +${formatBRL(costs.modeling_organic_fee_cents ?? 0)}` : ''}
                        {t.value === 'parametric' && (costs.modeling_parametric_fee_cents ?? 0) > 0 ? ` — +${formatBRL(costs.modeling_parametric_fee_cents ?? 0)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-ink-soft leading-snug">
                  <span className="font-medium text-ink-muted">Por que o arquivo é obrigatório?</span> Mesmo na modelagem, o dono
                  precisa de uma <span className="font-medium">referência ou desenho técnico</span> (foto, croqui, esboço, PDF ou
                  medidas) para entender o que você quer e orçar corretamente. Sem isso não há como modelar o projeto certo.
                </p>
              </div>
            )}
          </div>
        )}
        {fileBlock}
      </div>
      )}

      {!atestado && (
      <div className="rounded-xl bg-surface-subtle p-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-ink-muted">Máquina ({hours}h)</span><span>{formatBRL(pricing.breakdown.machine_cents)}</span></div>
        <div className="flex justify-between"><span className="text-ink-muted">Filamento ({grams}g)</span><span>{formatBRL(pricing.breakdown.filament_cents)}</span></div>
        <div className="flex justify-between"><span className="text-ink-muted">Setup</span><span>{formatBRL(pricing.breakdown.setup_cents)}</span></div>
        {pricing.breakdown.delivery_cents > 0 && (
          <div className="flex justify-between"><span className="text-ink-muted">Entrega</span><span>{formatBRL(pricing.breakdown.delivery_cents)}</span></div>
        )}
        {pricing.breakdown.modeling_cents > 0 && (
          <div className="flex justify-between"><span className="text-ink-muted">Modelagem 3D</span><span>{formatBRL(pricing.breakdown.modeling_cents)}</span></div>
        )}
        <div className="flex justify-between border-t border-border pt-2 font-medium">
          <span>Subtotal</span><span>{formatBRL(pricing.supplier_amount_cents)}</span>
        </div>
        <div className="flex justify-between text-ink-muted text-xs">
          <span>Taxa plataforma (7%)</span><span>{formatBRL(pricing.platform_fee_cents)}</span>
        </div>
        <div className="flex justify-between font-bold text-lg border-t border-border pt-2">
          <span>Total</span><span className="text-brand-700">{formatBRL(pricing.total_amount_cents)}</span>
        </div>
      </div>
      )}

      {atestado ? (
        <button
          onClick={() => void submitReserve(
            0, 0, 0,
            { quantity, notes: notes.trim() || undefined,
              atestado_leigo: true, awaiting_owner_quote: true },
          )}
          disabled={atestadoNeedsFile || submitting}
          className="btn-primary w-full gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
          {submitting ? 'Enviando…' : 'Solicitar orçamento à farm'}
        </button>
      ) : (
        <button
          onClick={() => void submitReserve(
            pricing.supplier_amount_cents,
            pricing.platform_fee_cents,
            pricing.total_amount_cents,
            { material, hours_estimated: hours, filament_grams: grams, delivery_km: deliveryKm,
              machine_brand: selectedMachine?.brand, machine_model: selectedMachine?.model,
              machines_count: rentCount, wall_clock_hours: wallClockHours,
              quantity, colors: colors.trim() || undefined, notes: notes.trim() || undefined,
              modeling_requested: wantsModeling || undefined,
              modeling_type: wantsModeling ? modelingType : undefined,
              modeling_fee_cents: wantsModeling ? modelingFee : undefined },
          )}
          disabled={modelingNeedsFile || soldOut || submitting}
          className="btn-primary w-full gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
          {submitting ? 'Enviando…' : soldOut ? 'Máquina indisponível' : 'Solicitar cotação'}
        </button>
      )}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function FarmDetail() {
  const { slug = '' }                          = useParams<{ slug: string }>();
  const { data, loading, error }               = useFarm(slug);
  const { createOrder, loading: ordering }     = useFarmOrder();
  const { user }                               = useAuth();
  const navigate                               = useNavigate();

  const [activeTab, setActiveTab]   = useState<'sobre' | 'maquinas' | 'materiais' | 'avaliacoes'>('sobre');
  const [chatLoading, setChatLoading] = useState(false);
  const [lightbox, setLightbox]     = useState<string | null>(null);
  const [orderErr, setOrderErr]     = useState('');
  // Denúncia de má conduta
  const [reporting, setReporting]   = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg]   = useState<{ ok: boolean; text: string } | null>(null);
  const photoInputRef               = useRef<HTMLInputElement>(null);

  const farmId = data?.farm.id ?? '';
  const { photos, uploading, uploadPhoto, deletePhoto } = useFarmPhotos(farmId);
  const isOwner = !!user && !!data && user.id === data.farm.owner_user_id;

  async function handleChat() {
    if (!data || !user) return;
    setChatLoading(true);
    try {
      const threadId = await getOrCreateThread(data.farm.id, data.farm.owner_user_id);
      navigate(`/farms/chat/${threadId}`);
    } finally {
      setChatLoading(false);
    }
  }

  async function handleReserve(
    supplier: number, fee: number, total: number,
    specs: Record<string, unknown>, projectFile: File | null,
  ) {
    if (!data) return;
    setOrderErr('');
    try {
      // 1) Sobe o arquivo do projeto (se houver) para o bucket quote-files
      if (projectFile) {
        const path = `${data.farm.id}/${Date.now()}-${projectFile.name.replace(/[^\w.\-]/g, '_')}`;
        const { error: upErr } = await supabase.storage
          .from('quote-files')
          .upload(path, projectFile, { contentType: 'application/octet-stream' });
        if (upErr) throw new Error(`Falha ao enviar o arquivo: ${upErr.message}`);
        const { data: pub } = supabase.storage.from('quote-files').getPublicUrl(path);
        specs.project_file_url = pub.publicUrl;
        specs.project_file_name = projectFile.name;
      }

      const order = await createOrder({
        farm_id:               data.farm.id,
        specs,
        supplier_amount_cents: supplier,
        platform_fee_cents:    fee,
        total_amount_cents:    total,
        initialStatus:         'quote',
      });
      navigate(`/farms/pedidos/${order.id}`);
    } catch (e: any) {
      setOrderErr(e.message ?? 'Erro ao criar pedido. Tente novamente.');
    }
  }

  if (loading) return (
    <PageShell title="Carregando…" back="/farms">
      <div className="h-64 rounded-2xl bg-surface-subtle animate-pulse" />
    </PageShell>
  );
  if (error || !data) return (
    <PageShell title="Farm não encontrada" back="/farms">
      <EmptyState icon={Printer} title={error || 'Farm não encontrada'} />
    </PageShell>
  );

  const { farm, location, machines, materials, specializations, costs, shift, reviews, avg_rating, reviews_count, reservations } = data;

  return (
    <PageShell title={farm.name} back="/farms" maxWidth="xl">

      {/* Header */}
      <div className="card p-5 mb-6 flex flex-col sm:flex-row gap-4">
        {farm.logo_url
          ? <img src={farm.logo_url} alt={farm.name} className="w-20 h-20 rounded-2xl object-cover shrink-0" />
          : <div className="w-20 h-20 rounded-2xl bg-brand-50 flex items-center justify-center text-4xl shrink-0">🖨️</div>
        }
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-xl text-ink">{farm.name}</h2>
          {location && (
            <p className="text-sm text-ink-muted flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5" />{location.city}, {location.state_uf}
            </p>
          )}
          {shift && (
            <p className="text-xs text-ink-soft flex items-center gap-1 mt-1">
              <Clock className="w-3 h-3" />
              {shift.runs_24h ? '24h / 7 dias' : `${shift.work_hours_per_day}h/dia · ${shift.work_days_per_week} dias/sem`}
            </p>
          )}
          {costs?.delivery_offered && (
            <p className="text-xs text-positive flex items-center gap-1 mt-1">
              <Truck className="w-3 h-3" />Entrega disponível até {costs.delivery_radius_km} km
            </p>
          )}
          <div className="flex items-center gap-1 mt-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < Math.round(avg_rating) ? 'text-warning fill-warning' : 'text-border'}`} />
            ))}
            <span className="text-sm text-ink-muted ml-1">{avg_rating.toFixed(1)} ({reviews_count} avaliações)</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {isOwner ? (
            <span className="chip bg-brand-50 text-brand-800 text-center">Você é o dono desta farm</span>
          ) : (
            <>
              <button onClick={handleChat} disabled={chatLoading} className="btn-secondary gap-2">
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                Iniciar conversa
              </button>
              <button
                onClick={() => { setReporting(true); setReportMsg(null); }}
                className="btn-ghost !py-1.5 !text-xs gap-1.5 text-ink-muted hover:text-negative"
                title="Denunciar má conduta desta farm ao suporte"
              >
                <Flag className="w-3.5 h-3.5" />
                Denunciar farm
              </button>
            </>
          )}
          {ordering && <p className="text-xs text-ink-muted text-center">Criando pedido…</p>}
          {orderErr && (
            <div className="flex items-center gap-2 text-xs text-negative bg-negative/10 border border-negative/20 rounded-xl px-3 py-2 mt-1">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />{orderErr}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-border">
            {(['sobre','maquinas','materiais','avaliacoes'] as const).map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                  activeTab === tab ? 'border-brand-700 text-brand-800' : 'border-transparent text-ink-muted hover:text-ink'
                }`}>
                {tab === 'maquinas' ? 'Máquinas'
                  : tab === 'avaliacoes' ? `Avaliações${reviews_count > 0 ? ` (${reviews_count})` : ''}`
                  : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Sobre */}
          {activeTab === 'sobre' && (
            <div className="card p-5 space-y-4">
              <p className="text-sm text-ink">{farm.description || 'Sem descrição.'}</p>
              {specializations.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-ink-muted mb-2">Especializações</p>
                  <div className="flex flex-wrap gap-2">
                    {specializations.map((s) => {
                      const label = SPECIALIZATIONS.find((x) => x.value === s)?.label ?? s;
                      return <span key={s} className="chip bg-brand-50 text-brand-800">{label}</span>;
                    })}
                  </div>
                </div>
              )}
              {costs && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 border-t border-border">
                  <div className="text-center">
                    <p className="text-xs text-ink-muted">Custo/hora</p>
                    <p className="font-semibold text-ink">{formatBRL(costs.hourly_machine_cost_cents)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-ink-muted">Setup</p>
                    <p className="font-semibold text-ink">{formatBRL(costs.setup_fee_cents)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-ink-muted">Markup filamento</p>
                    <p className="font-semibold text-ink">{costs.filament_markup_pct}%</p>
                  </div>
                </div>
              )}

              {/* Galeria de fotos */}
              {(photos.length > 0 || isOwner) && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-ink-muted">
                      Fotos da farm {photos.length > 0 && `(${photos.length})`}
                    </p>
                    {isOwner && (
                      <>
                        <button
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploading}
                          className="btn-secondary !py-1 !px-2.5 !text-xs gap-1"
                        >
                          {uploading
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Camera className="w-3 h-3" />
                          }
                          Adicionar foto
                        </button>
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          className="hidden"
                          onChange={async (e) => {
                            for (const file of Array.from(e.target.files ?? [])) {
                              await uploadPhoto(file);
                            }
                            e.target.value = '';
                          }}
                        />
                      </>
                    )}
                  </div>

                  {photos.length === 0 && isOwner && (
                    <p className="text-xs text-ink-muted text-center py-4 border-2 border-dashed border-border rounded-xl">
                      Nenhuma foto ainda — clique em "Adicionar foto" para mostrar sua farm aos clientes.
                    </p>
                  )}

                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {photos.map((p) => (
                        <div key={p.id} className="relative group aspect-square">
                          <img
                            src={p.url}
                            alt="Foto da farm"
                            className="w-full h-full object-cover rounded-xl cursor-pointer"
                            onClick={() => setLightbox(p.url)}
                          />
                          {isOwner && (
                            <button
                              onClick={() => void deletePhoto(p.id, p.storage_path)}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Máquinas */}
          {activeTab === 'maquinas' && (
            <div className="space-y-3">
              {machines.length === 0
                ? <p className="text-sm text-ink-muted">Sem máquinas cadastradas.</p>
                : machines.map((m) => (
                  <div key={m.id} className="card p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-surface-subtle flex items-center justify-center text-xl shrink-0">🖨️</div>
                    <div>
                      <p className="font-semibold text-ink">
                        {m.brand} {m.model}
                        <span className="text-ink-muted font-normal"> ×{m.quantity}</span>
                        {(() => {
                          const rsv = reservations[`${m.brand}|||${m.model}`] ?? 0;
                          return rsv > 0
                            ? <span className="chip chip-warning ml-2 !py-0 !text-[11px]">{rsv} reservada{rsv !== 1 ? 's' : ''}</span>
                            : null;
                        })()}
                      </p>
                      {m.build_volume_x_mm != null && m.build_volume_y_mm != null && m.build_volume_z_mm != null && (
                        <p className="text-xs text-ink-soft mt-0.5">
                          {m.build_volume_x_mm}×{m.build_volume_y_mm}×{m.build_volume_z_mm} mm
                        </p>
                      )}
                      {m.capabilities.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {m.capabilities.map((c) => <span key={c} className="chip bg-surface-subtle text-ink-muted text-[11px]">{c}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {/* Materiais */}
          {activeTab === 'materiais' && (
            <div className="card p-5">
              {materials.length === 0
                ? <p className="text-sm text-ink-muted">Sem materiais cadastrados.</p>
                : <div className="flex flex-wrap gap-2">
                    {materials.map((mat) => {
                      const label = MATERIALS.find((m) => m.value === mat)?.label ?? mat;
                      return <span key={mat} className="chip bg-surface-subtle text-ink font-medium">{label}</span>;
                    })}
                  </div>
              }
            </div>
          )}

          {/* Avaliações */}
          {activeTab === 'avaliacoes' && (
            <div className="card p-5 space-y-4">
              {reviews.length === 0 ? (
                <p className="text-sm text-ink-muted text-center py-4">
                  Nenhuma avaliação ainda. As avaliações aparecem após pedidos concluídos.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-3 pb-3 border-b border-border">
                    <span className="text-4xl font-bold text-ink">{avg_rating.toFixed(1)}</span>
                    <div>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-5 h-5 ${i < Math.round(avg_rating) ? 'text-warning fill-warning' : 'text-border'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-ink-muted mt-0.5">{reviews_count} avaliação{reviews_count !== 1 ? 'ões' : ''}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {reviews.map((r) => (
                      <div key={r.id} className="border-b border-border pb-3 last:border-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'text-warning fill-warning' : 'text-border'}`} />
                            ))}
                          </div>
                          <span className="text-xs text-ink-muted">{formatDate(r.created_at)}</span>
                        </div>
                        {r.comment && <p className="text-sm text-ink">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Simulador — oculto para o dono (não faz sentido pedir orçamento a si mesmo) */}
        {machines.length > 0 && !isOwner && (
          <Simulator
            costs={costs}
            machines={machines}
            reservations={reservations}
            offersModeling={!!farm.offers_modeling}
            onReserve={handleReserve}
          />
        )}
      </div>

      {/* Modal de denúncia */}
      {reporting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !reportBusy && setReporting(false)}>
          <div className="card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-ink flex items-center gap-2">
                <Flag className="w-4 h-4 text-negative" />
                Denunciar {farm.name}
              </h3>
              <button onClick={() => setReporting(false)} disabled={reportBusy} className="text-ink-muted hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>

            {reportMsg ? (
              <>
                <div className={`rounded-xl px-4 py-3 text-sm ${reportMsg.ok ? 'bg-positive/10 text-positive border border-positive/20' : 'bg-negative/10 text-negative border border-negative/20'}`}>
                  {reportMsg.text}
                </div>
                <button onClick={() => setReporting(false)} className="btn-secondary w-full">Fechar</button>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-muted">
                  Descreva o que aconteceu. Sua denúncia será registrada e enviada ao suporte
                  para análise. A descrição é obrigatória.
                </p>
                <textarea
                  className="input w-full"
                  rows={5}
                  maxLength={2000}
                  placeholder="Descreva a má conduta com o máximo de detalhes (mínimo 10 caracteres)…"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      setReportBusy(true);
                      try {
                        const { data: resp, error } = await supabase.functions.invoke('report-farm', {
                          body: { farm_id: farm.id, reason: reportReason.trim() },
                        });
                        if (error) throw new Error(error.message);
                        if (!resp?.ok) throw new Error(resp?.error ?? 'Falha ao enviar denúncia.');
                        setReportMsg({
                          ok: true,
                          text: `Denúncia registrada (protocolo ${String(resp.protocol).slice(0, 8)}). ${resp.email_sent ? 'O suporte foi notificado por e-mail.' : 'O suporte fará a análise em breve.'}`,
                        });
                        setReportReason('');
                      } catch (e: any) {
                        setReportMsg({ ok: false, text: e.message ?? 'Erro ao enviar denúncia.' });
                      } finally {
                        setReportBusy(false);
                      }
                    }}
                    disabled={reportBusy || reportReason.trim().length < 10}
                    className="btn-primary flex-1 gap-2"
                  >
                    {reportBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {reportBusy ? 'Enviando…' : 'Enviar denúncia'}
                  </button>
                  <button onClick={() => setReporting(false)} disabled={reportBusy} className="btn-secondary">
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt="Foto ampliada"
            className="max-w-full max-h-full rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </PageShell>
  );
}
